import { counselSchema } from "@/features/case-evaluation/types/counsel";
import { defendantFormSchema } from "@/features/case-evaluation/types/defendant";
import { caseEvidenceCaseFilingSchema } from "@/features/case-evaluation/types/evidence";
import { boolean, z } from "zod";
import { caseCategorySchema } from "./case-category";
import { mediaSchema } from "./media";

export const caseFilingAttachmentFormSchema = z.object({
	id: z.string(),
	files: z.array(mediaSchema),
	annexes: z.array(mediaSchema).optional(),
	payment: z.array(mediaSchema).optional(),
});

export const caseTypeSchema = z.object({
	id: z.string().optional(),
	name: z.string(),
	code: z.string(),
	case_category_id: z.string().optional(),
	status: z.string().optional(),
	case_category: caseCategorySchema.optional(),
	is_visible: boolean().default(true),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
});

export const caseFilingInfoFormSchema = z.object({
	case_number: z.string(),
	collection_type: z.string().min(1, { message: "Required" }),
	court_level: z.string().min(5, { message: "Required" }),
	court_type: z.string(),
	region_code: z.string().nullable(),
	zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	claim_amount: z.preprocess((val) => (val === "" || val === null ? null : Number(val)), z.number().max(999999.99)),
	city_code: z.string(),
	province_code: z.string(),
	court_station: z.string().min(1, { message: "Required" }),
	case_category: z.string(),
	case_type: z.string().min(1, { message: "Required" }),
	case_title: z.string().min(1, { message: "Required" }),
	first_name: z.string().optional(),
	address: z.string().optional(),
	last_name: z.string().optional(),
	middle_name: z.string().optional(),
	address_1: z.string().optional(),
	address_2: z.string().optional(),
	barangay: z.string().optional(),
	corporation: z.string().optional(),
	classification: z.string().optional(),
	id: z.string().optional(),
	party_type: z.string(),
	service_type: z.string(),
	email: z.string().optional(),
	plaintiff: z.object({
		email: z.string().optional(),
		classification: z.string().optional(),
		landline: z.string().optional().nullable(),
		mobile_number: z.string().optional(),
		corporation: z.string().optional(),
		first_name: z.string().optional(),
		middle_name: z.string().optional(),
		last_name: z.string().optional(),
		region_code: z.string(),
		province_code: z.string(),
		city_code: z.string(),
		barangay: z.string(),
		address_1: z.string(),
		address_2: z.string().optional(),
		zipcode: z.union([z.string(), z.number()]).nullable().optional(),
		suffix: z.string().optional(),
		representatives: z
			.array(
				z.object({
					id: z.string().optional(),
					name: z.string().nullable().optional(),
					email: z
						.union([z.string().email(), z.literal(""), z.null()])
						.optional()
						.nullable(),
				}),
			)
			.optional(),
	}),
	counsel_suffix: z.string().optional(),
	counsel_first_name: z.string().nullable().optional(),
	counsel_last_name: z.string().nullable().optional(),
	counsel_email: z.any().optional().nullable(),
	counsel_middle_name: z.string().optional().nullable(),
	counsel_region_code: z.string().optional().nullable(),
	counsel_province_code: z.string().optional().nullable(),
	counsel_city_code: z.string().optional().nullable(),
	counsel_roll_number: z.string().optional().nullable(),
	counsel_barangay: z.string().optional().nullable(),
	counsel_address_1: z.string().optional().nullable(),
	counsel_address_2: z.string().optional().nullable(),
	counsel_zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	lawfirm_region_code: z.string().optional().nullable(),
	lawfirm_province_code: z.string().optional(),
	lawfirm_city_code: z.string().optional(),
	lawfirm_barangay: z.string().optional(),
	lawfirm_address_1: z.string().optional(),
	lawfirm_address_2: z.string().optional(),
	lawfirm_zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	counsel_law_firm: z.string().optional(),
	counsel_law_firm_email: z.union([z.string().email(), z.null()]).optional(),
	nature_of_action: z.string().min(1, { message: "Required" }),
	counsel_mobile_number: z.string().optional(),
	counsel_landline: z.string().optional().nullable(),
	with_fee_exemption: z.boolean().optional().nullable(),
	plaintiffs: z.array(defendantFormSchema).min(1, { message: "At least 1 plaintiff is required" }),
	defendants: z.array(defendantFormSchema).min(1, { message: "At least 1 defendant is required" }),
	counsels: z.array(counselSchema).optional(),
	user: z.array(counselSchema).optional(),
	advance_information: z.boolean().default(false),
	filed_at: z.string(),
	received_by: z.string().min(1, { message: "Required" }),
	received_at: z.string(),
	raffled_at: z.string(),
	raffled_by: z.string().optional(),
	branch_id: z.string().optional(),
	summons_count: z.string().optional().nullable(),
});

export const caseFilingFormSchema = z.object({
	case_number: z.string().optional(),
	collection_type: z.string().min(1, { message: "Required" }),
	court_level: z.string().min(5, { message: "Required" }),
	court_type: z.string(),
	region_code: z.string().nullable(),
	zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	claim_amount: z.preprocess((val) => (val === "" || val === null ? null : Number(val)), z.number().max(999999.99)),
	city_code: z.string(),
	province_code: z.string(),
	court_station: z.string().min(1, { message: "Required" }),
	case_category: z.string(),
	case_type: z.string().min(1, { message: "Required" }),
	case_title: z.string().min(1, { message: "Required" }),
	first_name: z.string().optional(),
	address: z.string().optional(),
	last_name: z.string().optional(),
	middle_name: z.string().optional(),
	address_1: z.string().optional(),
	address_2: z.string().optional(),
	barangay: z.string().optional(),
	corporation: z.string().optional(),
	classification: z.string().optional(),
	id: z.string().optional(),
	party_type: z.string(),
	service_type: z.string(),
	email: z.string().optional(),
	plaintiff: z.object({
		email: z.string().optional(),
		classification: z.string().optional(),
		landline: z.string().optional().nullable(),
		mobile_number: z.string().optional(),
		corporation: z.string().optional(),
		first_name: z.string().optional(),
		middle_name: z.string().optional(),
		last_name: z.string().optional(),
		region_code: z.string(),
		province_code: z.string(),
		city_code: z.string(),
		barangay: z.string(),
		address_1: z.string(),
		address_2: z.string().optional(),
		zipcode: z.union([z.string(), z.number()]).nullable().optional(),
		suffix: z.string().optional(),
		representatives: z
			.array(
				z.object({
					id: z.string().optional(),
					name: z.string().nullable().optional(),
					email: z
						.union([z.string().email(), z.literal(""), z.null()])
						.optional()
						.nullable(),
				}),
			)
			.optional(),
	}),
	counsel_suffix: z.string().optional(),
	counsel_first_name: z.string().nullable().optional(),
	counsel_last_name: z.string().nullable().optional(),
	counsel_email: z.any().optional().nullable(),
	counsel_middle_name: z.string().optional().nullable(),
	counsel_region_code: z.string().optional().nullable(),
	counsel_province_code: z.string().optional().nullable(),
	counsel_city_code: z.string().optional().nullable(),
	counsel_roll_number: z.string().optional().nullable(),
	counsel_barangay: z.string().optional().nullable(),
	counsel_address_1: z.string().optional().nullable(),
	counsel_address_2: z.string().optional().nullable(),
	counsel_zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	lawfirm_region_code: z.string().optional().nullable(),
	lawfirm_province_code: z.string().optional(),
	lawfirm_city_code: z.string().optional(),
	lawfirm_barangay: z.string().optional(),
	lawfirm_address_1: z.string().optional(),
	lawfirm_address_2: z.string().optional(),
	lawfirm_zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	counsel_law_firm: z.string().optional(),
	counsel_law_firm_email: z.union([z.string().email(), z.null()]).optional(),
	nature_of_action: z.string().min(1, { message: "Required" }),
	counsel_mobile_number: z.string().optional(),
	counsel_landline: z.string().optional().nullable(),
	with_fee_exemption: z.boolean(),
	plaintiffs: z.array(defendantFormSchema).min(1, { message: "At least 1 plaintiff is required" }),
	defendants: z.array(defendantFormSchema).min(1, { message: "At least 1 defendant is required" }),
	annexes: z.array(mediaSchema).optional(), //request for medias
	payment: z.array(mediaSchema).optional(),
	medias: z.array(mediaSchema).optional(),
	counsels: z.array(counselSchema).optional(),
	user: z.array(counselSchema).optional(),
	evidences: z.array(caseEvidenceCaseFilingSchema).min(1, { message: "At least 1 evidence is required" }),
	advance_information: z.boolean().default(false),
	filed_at: z.string().optional(),
	received_by: z.string().min(1, { message: "Required" }),
	received_at: z.string().optional(),
	raffled_at: z.string().optional(),
	raffled_by: z.string().optional(),
	branch_id: z.string().optional(),
	summons_count: z.string().optional().nullable(),
});

export const caseFilingOccFormSchema = z.object({
	case_number: z.string().optional(),
	collection_type: z.string().min(1, { message: "Required" }),
	court_level: z.string().min(5, { message: "Required" }),
	court_type: z.string(),
	region_code: z.string().nullable(),
	zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	claim_amount: z.preprocess((val) => (val === "" || val === null ? null : Number(val)), z.number().max(999999.99)),
	city_code: z.string(),
	province_code: z.string(),
	court_station: z.string().min(1, { message: "Required" }),
	case_category: z.string(),
	case_type: z.string().min(1, { message: "Required" }),
	case_title: z.string().min(1, { message: "Required" }),
	first_name: z.string().optional(),
	address: z.string().optional(),
	last_name: z.string().optional(),
	middle_name: z.string().optional(),
	address_1: z.string().optional(),
	address_2: z.string().optional(),
	barangay: z.string().optional(),
	corporation: z.string().optional(),
	classification: z.string().optional(),
	id: z.string().optional(),
	party_type: z.string(),
	service_type: z.string(),
	email: z.string().optional(),
	plaintiff: z.object({
		email: z.string().optional(),
		classification: z.string().optional(),
		landline: z.string().optional().nullable(),
		mobile_number: z.string().optional(),
		corporation: z.string().optional(),
		first_name: z.string().optional(),
		middle_name: z.string().optional(),
		last_name: z.string().optional(),
		region_code: z.string(),
		province_code: z.string(),
		city_code: z.string(),
		barangay: z.string(),
		address_1: z.string(),
		address_2: z.string().optional(),
		zipcode: z.union([z.string(), z.number()]).nullable().optional(),
		suffix: z.string().optional(),
		representatives: z
			.array(
				z.object({
					id: z.string().optional(),
					name: z.string().nullable().optional(),
					email: z
						.union([z.string().email(), z.literal(""), z.null()])
						.optional()
						.nullable(),
				}),
			)
			.optional(),
	}),
	counsel_suffix: z.string().optional(),
	counsel_first_name: z.string().nullable().optional(),
	counsel_last_name: z.string().nullable().optional(),
	counsel_email: z.any().optional().nullable(),
	counsel_middle_name: z.string().optional().nullable(),
	counsel_region_code: z.string().optional().nullable(),
	counsel_province_code: z.string().optional().nullable(),
	counsel_city_code: z.string().optional().nullable(),
	counsel_roll_number: z.string().optional().nullable(),
	counsel_barangay: z.string().optional().nullable(),
	counsel_address_1: z.string().optional().nullable(),
	counsel_address_2: z.string().optional().nullable(),
	counsel_zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	lawfirm_region_code: z.string().optional().nullable(),
	lawfirm_province_code: z.string().optional(),
	lawfirm_city_code: z.string().optional(),
	lawfirm_barangay: z.string().optional(),
	lawfirm_address_1: z.string().optional(),
	lawfirm_address_2: z.string().optional(),
	lawfirm_zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	counsel_law_firm: z.string().optional(),
	counsel_law_firm_email: z.union([z.string().email(), z.null()]).optional(),
	nature_of_action: z.string().min(1, { message: "Required" }),
	counsel_mobile_number: z.string().optional(),
	counsel_landline: z.string().optional().nullable(),
	with_fee_exemption: z.boolean(),
	plaintiffs: z.array(defendantFormSchema).min(1, { message: "At least 1 plaintiff is required" }),
	defendants: z.array(defendantFormSchema).min(1, { message: "At least 1 defendant is required" }),
	annexes: z.array(mediaSchema).optional(), //request for medias
	payment: z.array(mediaSchema).optional(),
	medias: z.array(mediaSchema).optional(),
	counsels: z.array(counselSchema).optional(),
	user: z.array(counselSchema).optional(),
	evidences: z.array(caseEvidenceCaseFilingSchema).min(1, { message: "At least 1 evidence is required" }),
	advance_information: z.boolean().default(false),
	filed_at: z.string().optional(),
	received_by: z.string().optional(),
	received_at: z.string().optional(),
	raffled_at: z.string().optional(),
	raffled_by: z.string().optional(),
	branch_id: z.string().optional(),
	summons_count: z.string().optional().nullable(),
});

export type CaseType = z.infer<typeof caseTypeSchema>;
export type CaseFilingFormSchema = z.infer<typeof caseFilingFormSchema>;
export type CaseFilingOccFormSchema = z.infer<typeof caseFilingOccFormSchema>;
export type CaseFilingInfoFormSchema = z.infer<typeof caseFilingInfoFormSchema>;
export type CaseFilingAttachmentFormSchema = z.infer<typeof caseFilingAttachmentFormSchema>;
